import React, {useEffect} from 'react';
import { SpecificFeeTypes, NoticePeriodFee, FOV2WeeklyVehicleFee, FOV2_1WeeklyVehicleFee, PaymentHolidayGapFee, AncillaryFee, NbpEarlyBreakFee, LongFeeNames } from '@services/subscription/SubscriptionService';
import { countrySpecificCurrency, convertMinorToMajorCurrency } from '@utils/misc/functions';
import { Country } from '@utils/constants/localisation';

type Props = {
    charge: SpecificFeeTypes;
    country: Country;
};

const boxStyle: React.CSSProperties = { border: '1px solid #e0e0e0', padding: 12, borderRadius: 6 };

// Type guards using long_fee_name as the primary discriminator
const isNoticePeriod = (c: SpecificFeeTypes): c is NoticePeriodFee => {
    return c.long_fee_name === LongFeeNames.NoticePeriodFee;
};

const isFOV2Weekly = (c: SpecificFeeTypes): c is FOV2WeeklyVehicleFee => {
    return c.long_fee_name === LongFeeNames.WeeklyVehicleChargeFee;
};

const isFOV2_1Weekly = (c: SpecificFeeTypes): c is FOV2_1WeeklyVehicleFee => {
    return c.long_fee_name === LongFeeNames.WeeklyVehicleChargeFee_2_1;
};

const isPaymentHoliday = (c: SpecificFeeTypes): c is PaymentHolidayGapFee => {
    return c.long_fee_name === LongFeeNames.PaymentHolidayGapFee;
};

const isAncillary = (c: SpecificFeeTypes): c is AncillaryFee => {
    return c.long_fee_name == LongFeeNames.AncillaryFee;
};

const isNbpEarlyBreakFee = (c: SpecificFeeTypes): c is NbpEarlyBreakFee => {
    return c.long_fee_name === LongFeeNames.NbpEarlyBreakFee;
};

// Reusable component for right-aligned value rows
type DetailRowProps = {
    label: React.ReactNode;
    value: React.ReactNode;
};

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '2px 0' }}>
        <span><b>{label}</b></span>
        <span style={{ textAlign: 'right', minWidth: 80 }}>{value}</span>
    </div>
);

export default function ChargeCard({ charge, country }: Props) {
    // Display a cancellation charge. Contains logic for the specific cancellation types
    const total = (charge.fee || 0) * (charge.quantity || 1);
    const currency = countrySpecificCurrency(country);

    useEffect(() => {
        if (!isAncillary(charge) && charge.quantity !== 1) {
            console.warn(`ChargeCard: Non-ancillary charge with quantity != 1. Charge type: ${charge.long_fee_name}, quantity: ${charge.quantity}`);
        }
    }, [charge]);

    return (
        <div style={boxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div > Please confirm <strong>{charge.long_fee_name}</strong></div>
            </div>

            <div style={{ marginTop: 8 }}>
                {isNoticePeriod(charge) && (
                    <div style={{ marginTop: 6 }}>
                        <DetailRow label="Days charged for notice period breach:" value={charge.days_charged} />
                        <DetailRow label="Daily subscription fee:" value={currency + ' ' + convertMinorToMajorCurrency(charge.daily_subscription_fee)} />
                    </div>
                )}

                {isFOV2Weekly(charge) && (
                    <div style={{ marginTop: 6 }}>
                        <DetailRow label="Weekly A1 component fee:" value={currency + ' ' + convertMinorToMajorCurrency(charge.weekly_a1_component_fee)} />
                        <DetailRow label="Termination weeks multiplier:" value={charge.termination_weeks_multiplier} />
                        <DetailRow label="Vehicle Re-marketing fee:" value={currency + ' ' + convertMinorToMajorCurrency(charge.vehicle_remarketing_fee)} />
                        <DetailRow label="Weeks payout protection paid:" value={charge.weeks_payout_protection_fee_paid} />
                    </div>
                )}

                {isFOV2_1Weekly(charge) && (
                    <div style={{ marginTop: 6 }}>
                        <DetailRow label="Weekly A1 component fee:" value={currency + ' ' + convertMinorToMajorCurrency(charge.weekly_a1_component_fee)} />
                        <DetailRow label="Termination weeks multiplier:" value={charge.termination_weeks_multiplier} />
                        <DetailRow label="Vehicle Re-marketing fee:" value={currency + ' ' + convertMinorToMajorCurrency(charge.vehicle_remarketing_fee)} />
                        <DetailRow label="Weeks payout protection paid:" value={charge.weeks_payout_protection_fee_paid} />
                        {charge.initial_contribution != null && (
                            <DetailRow label="Initial contribution:" value={currency + ' ' + convertMinorToMajorCurrency(charge.initial_contribution)} />
                        )}
                        {charge.term_length_weeks != null && (
                            <DetailRow label="Term length (weeks):" value={charge.term_length_weeks} />
                        )}
                        {charge.term_remaining_weeks != null && (
                            <DetailRow label="Term remaining (weeks):" value={charge.term_remaining_weeks} />
                        )}
                    </div>
                )}

                {isPaymentHoliday(charge) && (
                    <div style={{ marginTop: 6 }}>
                        This fee covers the shortfall between the amount the customer has paid during Payment Holidays,
                        and their normal weekly fee (for the period covered by Payment Holidays).
                        <div style={{marginTop: 6}}></div>
                        <DetailRow label="Total PH weeks taken:" value={charge.total_ph_weeks_taken} />
                        <i>Weeks with a PH coupon applied</i>
                        <DetailRow label="Total PH contribution:" value={currency + ' ' + convertMinorToMajorCurrency(charge.total_ph_contribution)} />
                        <i>Sum of PH week contributions paid</i>
                        <DetailRow label="Total PH topups:" value={currency + ' ' + convertMinorToMajorCurrency(charge.total_ph_topups)} />
                        <i>Sum of PH topups paid, charged when the customer exceeds the milage during a PH week</i>
                        <DetailRow label="Total PH subscription fee:" value={currency + ' ' + convertMinorToMajorCurrency(charge.total_ph_subscription_fee)} />
                        <i>Expected amount if no PHs were taken</i>
                    </div>
                )}

                {isAncillary(charge) && (
                    <div style={{marginTop: 6}}>
                        <DetailRow label="Ancillary Charge Name:" value={charge.cb_addon_name} />
                        <DetailRow label="Unit price:" value={currency + ' ' + convertMinorToMajorCurrency((charge).unit_price)} />
                        <DetailRow label="Quantity:" value={charge.quantity} />
                    </div>
                )}

                {isNbpEarlyBreakFee(charge) && (
                    <div style={{ marginTop: 6 }}>
                        {charge.fee > 0 && charge.triggering_nbp_type && charge.triggering_nbp_end_date ? (
                            <>
                                <div>
                                    Cancelling during a <strong>{charge.triggering_nbp_type}</strong> commitment that ends <strong>{charge.triggering_nbp_end_date}</strong>.
                                </div>
                                <DetailRow label="Non-Break Period type:" value={charge.triggering_nbp_type} />
                                <DetailRow label="Non-Break Period ends:" value={charge.triggering_nbp_end_date} />
                            </>
                        ) : (
                            <i>No active Non-Break Period at the cancellation date — fee waived.</i>
                        )}
                    </div>
                )}
                {/*Margin*/}
                <div style={{ marginTop: 15}}/>

                {/*Total fee at bottom*/}
                <DetailRow label={<span style={{ fontWeight: 700 }}>Total Fee:</span>} value={<span style={{ fontWeight: 700 }}>{currency}{convertMinorToMajorCurrency(total)}</span>} />
            </div>
        </div>
    );
}
